import React from 'react'
import { Button, Input, Label } from '@/shared/ui'
import { type BybitInstrument } from '@/modules/market/services/bybitService'
import { insertCryptoAsset, type CryptoAsset, updateCryptoLogo, activateAssetSeed } from '@/modules/crypto-assets/services/cryptoAssetsService'
import { uploadLogoAsset } from '@/modules/upload/services/uploadService'
import { useToast } from '@/shared/ui/Toast'
import { runQuickBootstrap } from '@/modules/crypto-assets/services/bootstrapService'

type Props = {
  items: BybitInstrument[]
  existing: Set<string>
  onAdded?: (assets: CryptoAsset[]) => void
  listRef?: React.RefObject<HTMLDivElement | null>
}

type Sel = { symbol: string; name?: string | null; base?: string; quote?: string }

export default function BatchAddBybit({ items, existing, onAdded, listRef }: Props) {
  const { add: toast } = useToast()
  const [selectMap, setSelectMap] = React.useState<Record<string, Sel>>({})
  const [logoMap, setLogoMap] = React.useState<Record<string, File | undefined>>({})
  const [activate, setActivate] = React.useState(true)
  const [normalizeAll, setNormalizeAll] = React.useState(false)
  const [concurrency, setConcurrency] = React.useState(3)
  const [waveSize, setWaveSize] = React.useState(20)
  const [waveDelayMs, setWaveDelayMs] = React.useState(6000)
  const [running, setRunning] = React.useState(false)
  const [progress, setProgress] = React.useState({ done: 0, total: 0 })

  const visible = React.useMemo(() => items.filter(b => !existing.has(String(b.symbol || '').toUpperCase())), [items, existing])
  const selected = React.useMemo(() => Object.values(selectMap), [selectMap])
  const allSelectedVisible = selected.length > 0 && selected.length === visible.length

  function toggle(sym: string, sel?: Sel){
    setSelectMap(prev => {
      const cp = { ...prev }
      if (cp[sym]) delete cp[sym]; else cp[sym] = sel || { symbol: sym }
      return cp
    })
  }

  async function run() {
    const list = selected
    if (!list.length) return
    setRunning(true); setProgress({ done: 0, total: list.length })
    const added: CryptoAsset[] = []

    // Execução em ondas com limite de concorrência
    const chunks: Sel[][] = []
    for (let i = 0; i < list.length; i += waveSize) chunks.push(list.slice(i, i + waveSize))

    try {
      for (let w = 0; w < chunks.length; w++) {
        const wave = chunks[w]
        await pLimit(normalizeAll ? 1 : concurrency, wave.map((s) => async () => {
          const symbol = s.symbol
          const base = (s.base || symbol.replace(/(USDT|USDC|BTC)$/,''))
          const quote = (s.quote || (symbol.match(/USDT|USDC|BTC$/)?.[0] || 'USDT'))
          try {
            // 1) insert (idempotente: confiar no unique de symbol)
            let id: string | null = null
            try {
              const res = await insertCryptoAsset({ symbol, base_asset: base, quote_asset: quote, name: s.name || undefined, logo_url: null })
              id = res.id
            } catch (e: any) {
              // duplicate: simplesmente ignore, e tente localizar pelo símbolo via select
              id = null
            }
            // 2) logo opcional
            try {
              const f = logoMap[symbol]
              if (f && id) {
                const up = await uploadLogoAsset(symbol, f)
                await updateCryptoLogo(id, up.url)
              }
            } catch {}
            // 3) ativar opcionalmente
            if (activate) {
              try { await activateAssetSeed(symbol) } catch {}
            }
            if (normalizeAll) {
              try { await runQuickBootstrap(symbol, { target: 500 }) } catch {}
            }
            // feedback local
            added.push({ id: id || symbol, symbol, base_asset: base, quote_asset: quote, name: s.name || null, logo_url: undefined, is_active: activate }) as any
          } finally {
            setProgress(prev => ({ ...prev, done: prev.done + 1 }))
          }
        }))
        if (w < chunks.length - 1) await sleep(waveDelayMs)
      }
      toast({ title: 'Lote concluído', description: `${progress.total} moedas processadas`, variant: 'success' })
      onAdded?.(added)
      setSelectMap({}); setLogoMap({})
    } catch (e: any) {
      toast({ title: 'Lote', description: e?.message || 'Falha ao processar lote', variant: 'error' })
    } finally { setRunning(false) }
  }

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-end gap-2">
        <div className="flex items-center gap-2">
          <input id="chk-all" type="checkbox" className="h-4 w-4" checked={allSelectedVisible} onChange={(e)=>{
            if (e.currentTarget.checked) {
              const next: Record<string, Sel> = {}
              for (const b of visible) next[b.symbol] = { symbol: b.symbol, name: b.name || null, base: b.baseCoin, quote: b.quoteCoin }
              setSelectMap(next)
            } else { setSelectMap({}) }
          }} />
          <Label htmlFor="chk-all" className="text-xs">Selecionar visíveis</Label>
        </div>
        <div className="flex items-center gap-2">
          <input id="chk-act" type="checkbox" className="h-4 w-4" checked={activate} onChange={(e)=> setActivate(e.currentTarget.checked)} />
          <Label htmlFor="chk-act" className="text-xs">Ativar após adicionar</Label>
        </div>
        <div className="flex items-center gap-2">
          <input id="chk-norm" type="checkbox" className="h-4 w-4" checked={normalizeAll} onChange={(e)=> setNormalizeAll(e.currentTarget.checked)} />
          <Label htmlFor="chk-norm" className="text-xs">Normalizar 500 (9 TFs)</Label>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Label className="text-xs">Concorrência</Label>
          <Input type="number" className="h-8 w-16" min={1} max={6} value={concurrency} onChange={(e)=> setConcurrency(Math.max(1, Math.min(6, parseInt(e.target.value||'3',10))))} />
          <Label className="text-xs">Lote</Label>
          <Input type="number" className="h-8 w-16" min={5} max={30} value={waveSize} onChange={(e)=> setWaveSize(Math.max(5, Math.min(30, parseInt(e.target.value||'20',10))))} />
          <Label className="text-xs">Delay (ms)</Label>
          <Input type="number" className="h-8 w-24" min={0} max={60000} value={waveDelayMs} onChange={(e)=> setWaveDelayMs(Math.max(0, Math.min(60000, parseInt(e.target.value||'6000',10))))} />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="text-xs text-foreground/60">Selecionadas: <b>{selected.length}</b></div>
          <Button disabled={!selected.length || running} onClick={run}>{running ? `Processando ${progress.done}/${progress.total}` : 'Adicionar (lote)'}</Button>
        </div>
      </div>

      <div ref={listRef as any} className="grid max-h-96 grid-cols-1 gap-2 overflow-auto md:grid-cols-2 xl:grid-cols-3">
        {visible.map(b => (
          <div key={b.symbol} className={`flex items-center justify-between rounded-lg border ${selectMap[b.symbol] ? 'border-primary/60' : 'border-border/50'} bg-background/30 p-3 text-sm`}>
            <div className="flex min-w-0 items-center gap-3">
              <input type="checkbox" className="h-4 w-4" checked={!!selectMap[b.symbol]} onChange={()=> toggle(b.symbol, { symbol: b.symbol, name: b.name || null, base: b.baseCoin, quote: b.quoteCoin })} />
              <div className="min-w-0">
                <div className="truncate font-medium">{b.name || b.symbol.replace(/USDT$/,'')}</div>
                <div className="truncate text-xs text-foreground/60">{b.symbol}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                className="text-xs"
                accept="image/*"
                onChange={(e)=> {
                  const file = (e.currentTarget?.files && e.currentTarget.files.length) ? e.currentTarget.files[0] : undefined
                  setLogoMap(prev => ({ ...prev, [b.symbol]: file }))
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

async function pLimit<T>(limit: number, tasks: (() => Promise<T>)[]) {
  const out: T[] = []
  let i = 0
  const running: Promise<void>[] = []
  async function worker(){
    while (i < tasks.length) {
      const idx = i++
      out[idx] = await tasks[idx]()
    }
  }
  for (let k=0;k<Math.min(limit, tasks.length);k++) running.push(worker())
  await Promise.all(running)
  return out
}

function sleep(ms: number){ return new Promise(res => setTimeout(res, ms)) }

